import { PickerFileMetadata } from './../../node_modules/filestack-js/build/main/lib/picker.d';
import { StateCreator, create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { persist, createJSONStorage } from "zustand/middleware";
import { middlewares } from "./middlewares";
import { translationType } from "@/components/screen/get-quote/components/Checkout";
import { TRANSLATION_OPTIONS } from "@/components/screen/get-quote/components/Checkout/constants";

export type GetQuoteFields = {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  business?: boolean;
  translationType?: translationType;
  fromLanguage?: string;
  toLanguage?: string;
  files?: PickerFileMetadata[] | null;
  projectDescription?: string;
  consent?: boolean;
};

export type GetQuoteStoreState = {
  fields: GetQuoteFields;
  checkoutData: Record<string, unknown> | null;
};

type GetQuoteStoreActions = {
  setField: <T extends keyof GetQuoteStoreState>(
    field: T,
    value: GetQuoteStoreState[T]
  ) => void;
  setFields: (fields: Partial<GetQuoteStoreState>) => void;
  cleanUp: () => void;
  setCheckoutData: (data: Record<string, unknown>) => void;
  clearCheckoutData: () => void;
};

type GetQuoteStore = GetQuoteStoreState & GetQuoteStoreActions;

const initialState: GetQuoteStoreState = {
  fields: {
    firstName: "",
    lastName: "",
    email: "",
    business: false,
    translationType: TRANSLATION_OPTIONS.certified,
    fromLanguage: "",
    toLanguage: "",
    files: null,
    projectDescription: "",
    consent: true,
  },
  checkoutData: null,
};

const createStore: StateCreator<
  GetQuoteStore,
  [["zustand/devtools", never], ["zustand/immer", never]],
  [["zustand/persist", unknown]]
> = (set) => {
  return {
    ...initialState,
    setField(key, value) {
      set(
        (state) => {
          (state as unknown as GetQuoteStoreState)[key] = value as never;

          return state;
        },
        false,
        "getQuoteStore/setField"
      );
    },
    setFields(values) {
      set(
        (state) => {
          state = {
            ...state,
            ...values,
          };

          return state;
        },
        false,
        "getQuoteStore/setFields"
      );
    },
    setCheckoutData(data) {
      set(
        (state) => {
          state.checkoutData = {
            ...(state.checkoutData ?? {}),
            ...data,
          };
          return state;
        },
        false,
        "getQuoteStore/setCheckoutData"
      );
    },
    clearCheckoutData() {
      set(
        (state) => {
          state.checkoutData = null;
          return state;
        },
        false,
        "getQuoteStore/clearCheckoutData"
      );
    },
    cleanUp() {
      set(initialState, false, {
        type: "getQuoteStore/cleanUp",
      });
    },
  };
};

export const useGetQuoteStore = create<GetQuoteStore>(
  middlewares(
    "get-quote-store",
    immer(
      persist(createStore, {
        name: "get-quote-store",
        storage: createJSONStorage(() => {
          return localStorage;
        }),
      })
    )
  )
);
